// ============================================================================
// AD SYSTEM TYPES & MOCK DATA
// ============================================================================

// --- Types ---

export type AdTier = 'standard' | 'premium' | 'featured';
export type AdCategory = 'hotel' | 'flight' | 'car' | 'activity' | 'insurance' | 'general';
export type AdPage = 'community' | 'my_trips' | 'trip_details' | 'my_photos' | 'destinations';
export type AdPlacement = 'sidebar' | 'in_feed' | 'full_screen';
export type PartnerStatus = 'active' | 'paused' | 'terminated';
export type CampaignStatus = 'active' | 'paused' | 'scheduled' | 'ended';

export interface AdPartner {
    id: string;
    name: string;
    logo: string;
    website: string;
    status: PartnerStatus;
    contractStart: string;
    contractEnd: string;
    createdAt: string;
    updatedAt: string;
    updatedBy: string;
}

export interface AdCampaign {
    id: string;
    partnerId: string;
    name: string;
    tier: AdTier;
    category: AdCategory;
    status: CampaignStatus;
    pages: AdPage[];
    placements: AdPlacement[];
    startDate: string;
    endDate: string;
    budget?: number;
    spent?: number;
    impressions: number;
    clicks: number;
    createdAt: string;
    updatedAt: string;
    updatedBy: string;
}

export interface Ad {
    id: string;
    campaignId: string;
    partnerId: string;
    title: string;
    description: string;
    image: string;
    ctaText: string;
    tier: AdTier;
    category: AdCategory;
    // Inline content (opens in drawer - never leaves site)
    inlineContent: {
        headline: string;
        body: string;
        features?: string[];
        price?: string;
        originalPrice?: string;
        discount?: string;
        rating?: number;
        reviewCount?: number;
        images?: string[];
        // Internal action - no external URLs
        actionType: 'search' | 'book' | 'learn_more';
        actionParams?: Record<string, string>;
    };
    // Targeting
    targetDestinations?: string[];
    targetCategories?: AdCategory[];
    // Status
    enabled: boolean;
    createdAt: string;
    updatedAt: string;
}

export interface AdImpression {
    id: string;
    adId: string;
    campaignId: string;
    partnerId: string;
    userId?: string;
    sessionId: string;
    page: AdPage;
    placement: AdPlacement;
    timestamp: string;
}

export interface AdClick {
    id: string;
    adId: string;
    campaignId: string;
    partnerId: string;
    userId?: string;
    sessionId: string;
    page: AdPage;
    placement: AdPlacement;
    action: string;
    timestamp: string;
}

export interface AdAuditLog {
    id: string;
    entityType: 'campaign' | 'partner' | 'ad' | 'settings';
    entityId: string;
    action: 'create' | 'update' | 'delete' | 'enable' | 'disable';
    changes: {
        field: string;
        oldValue: any;
        newValue: any;
    }[];
    userId: string;
    userName: string;
    timestamp: string;
}

export interface AdSettings {
    // Global (controlled by main admin)
    globalEnabled: boolean;
    // Per-page (controlled by ads admin)
    pageSettings: Record<AdPage, boolean>;
    // Per-tier (controlled by ads admin)
    tierSettings: Record<AdTier, boolean>;
    // Rotation settings (seconds)
    rotationIntervals: Record<AdTier, number>;
    // Frequency caps
    maxImpressionsPerAdPerSession: number;
    delayBeforeFirstAd: number; // seconds
}

// --- Rotation Intervals (seconds) ---

export const DEFAULT_ROTATION_INTERVALS: Record<AdTier, number> = {
    premium: 45,
    standard: 30,
    featured: 60,
};

// --- Mock Partners ---

export const MOCK_PARTNERS: AdPartner[] = [
    {
        id: 'partner-1',
        name: 'Marriott International',
        logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/8/8b/Marriott_International_logo.svg/200px-Marriott_International_logo.svg.png',
        website: 'https://marriott.com',
        status: 'active',
        contractStart: '2025-01-01',
        contractEnd: '2025-12-31',
        createdAt: '2025-01-01T00:00:00Z',
        updatedAt: '2025-01-15T10:30:00Z',
        updatedBy: 'admin@percoro.com',
    },
    {
        id: 'partner-2',
        name: 'Delta Air Lines',
        logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/d/d1/Delta_logo.svg/200px-Delta_logo.svg.png',
        website: 'https://delta.com',
        status: 'active',
        contractStart: '2025-01-01',
        contractEnd: '2025-12-31',
        createdAt: '2025-01-01T00:00:00Z',
        updatedAt: '2025-01-10T14:20:00Z',
        updatedBy: 'admin@percoro.com',
    },
    {
        id: 'partner-3',
        name: 'Hertz',
        logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/4/48/Hertz_logo.svg/200px-Hertz_logo.svg.png',
        website: 'https://hertz.com',
        status: 'active',
        contractStart: '2025-02-01',
        contractEnd: '2025-12-31',
        createdAt: '2025-02-01T00:00:00Z',
        updatedAt: '2025-02-01T00:00:00Z',
        updatedBy: 'admin@percoro.com',
    },
    {
        id: 'partner-4',
        name: 'Viator',
        logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/4/4e/Viator_logo.svg/200px-Viator_logo.svg.png',
        website: 'https://viator.com',
        status: 'active',
        contractStart: '2025-01-15',
        contractEnd: '2026-01-14',
        createdAt: '2025-01-15T00:00:00Z',
        updatedAt: '2025-01-20T09:00:00Z',
        updatedBy: 'admin@percoro.com',
    },
    {
        id: 'partner-5',
        name: 'GetYourGuide',
        logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/9/9a/GetYourGuide_logo.svg/200px-GetYourGuide_logo.svg.png',
        website: 'https://getyourguide.com',
        status: 'active',
        contractStart: '2025-01-01',
        contractEnd: '2025-12-31',
        createdAt: '2025-01-01T00:00:00Z',
        updatedAt: '2025-01-05T11:45:00Z',
        updatedBy: 'admin@percoro.com',
    },
    {
        id: 'partner-6',
        name: 'Allianz Travel Insurance',
        logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/4/44/Allianz_logo.svg/200px-Allianz_logo.svg.png',
        website: 'https://allianz.com',
        status: 'paused',
        contractStart: '2025-01-01',
        contractEnd: '2025-06-30',
        createdAt: '2025-01-01T00:00:00Z',
        updatedAt: '2025-02-10T16:00:00Z',
        updatedBy: 'admin@percoro.com',
    },
];

// --- Mock Campaigns ---

export const MOCK_CAMPAIGNS: AdCampaign[] = [
    {
        id: 'campaign-1',
        partnerId: 'partner-1',
        name: 'Marriott Bonvoy Spring 2025',
        tier: 'premium',
        category: 'hotel',
        status: 'active',
        pages: ['my_trips', 'trip_details', 'destinations', 'my_photos'],
        placements: ['sidebar', 'in_feed'],
        startDate: '2025-01-01',
        endDate: '2026-06-30',
        budget: 50000,
        spent: 12500,
        impressions: 125000,
        clicks: 3750,
        createdAt: '2025-01-01T00:00:00Z',
        updatedAt: '2025-02-15T10:00:00Z',
        updatedBy: 'admin@percoro.com',
    },
    {
        id: 'campaign-2',
        partnerId: 'partner-2',
        name: 'Delta SkyMiles Promo',
        tier: 'premium',
        category: 'flight',
        status: 'active',
        pages: ['my_trips', 'trip_details', 'destinations', 'my_photos'],
        placements: ['sidebar', 'in_feed'],
        startDate: '2025-02-01',
        endDate: '2026-05-31',
        budget: 40000,
        spent: 8000,
        impressions: 95000,
        clicks: 2850,
        createdAt: '2025-02-01T00:00:00Z',
        updatedAt: '2025-02-18T14:30:00Z',
        updatedBy: 'admin@percoro.com',
    },
    {
        id: 'campaign-3',
        partnerId: 'partner-3',
        name: 'Hertz Gold Plus Rewards',
        tier: 'standard',
        category: 'car',
        status: 'active',
        pages: ['my_trips', 'trip_details', 'destinations', 'my_photos'],
        placements: ['sidebar', 'in_feed'],
        startDate: '2025-02-01',
        endDate: '2026-12-31',
        budget: 25000,
        spent: 3000,
        impressions: 45000,
        clicks: 900,
        createdAt: '2025-02-01T00:00:00Z',
        updatedAt: '2025-02-01T00:00:00Z',
        updatedBy: 'admin@percoro.com',
    },
    {
        id: 'campaign-4',
        partnerId: 'partner-4',
        name: 'Viator Featured Experiences',
        tier: 'featured',
        category: 'activity',
        status: 'active',
        pages: ['community', 'trip_details', 'destinations'],
        placements: ['full_screen', 'sidebar', 'in_feed'],
        startDate: '2025-01-15',
        endDate: '2026-12-31',
        budget: 60000,
        spent: 15000,
        impressions: 180000,
        clicks: 7200,
        createdAt: '2025-01-15T00:00:00Z',
        updatedAt: '2025-02-12T09:15:00Z',
        updatedBy: 'admin@percoro.com',
    },
    {
        id: 'campaign-5',
        partnerId: 'partner-5',
        name: 'GetYourGuide Local Tours',
        tier: 'standard',
        category: 'activity',
        status: 'active',
        pages: ['community', 'destinations'],
        placements: ['full_screen', 'in_feed'],
        startDate: '2025-01-01',
        endDate: '2026-12-31',
        budget: 30000,
        spent: 7500,
        impressions: 110000,
        clicks: 2750,
        createdAt: '2025-01-01T00:00:00Z',
        updatedAt: '2025-02-05T11:00:00Z',
        updatedBy: 'admin@percoro.com',
    },
    {
        id: 'campaign-6',
        partnerId: 'partner-6',
        name: 'Allianz Travel Protection',
        tier: 'standard',
        category: 'insurance',
        status: 'paused',
        pages: ['my_trips', 'trip_details'],
        placements: ['sidebar'],
        startDate: '2025-01-01',
        endDate: '2026-06-30',
        budget: 15000,
        spent: 2000,
        impressions: 25000,
        clicks: 375,
        createdAt: '2025-01-01T00:00:00Z',
        updatedAt: '2025-02-10T16:00:00Z',
        updatedBy: 'admin@percoro.com',
    },
];

// --- Mock Ads ---

export const MOCK_ADS: Ad[] = [
    // Marriott Ads
    {
        id: 'ad-1',
        campaignId: 'campaign-1',
        partnerId: 'partner-1',
        title: 'Marriott Bonvoy',
        description: 'Earn points on every stay. Book direct for the best rates.',
        image: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=400',
        ctaText: 'View Hotels',
        tier: 'premium',
        category: 'hotel',
        inlineContent: {
            headline: 'Exclusive Member Rates',
            body: 'Join Marriott Bonvoy and unlock exclusive member rates at over 8,000 hotels worldwide. Earn points on every stay that never expire.',
            features: ['Free WiFi at all properties', 'Mobile check-in & room key', 'Member-only rates', 'Points never expire'],
            price: 'From $129/night',
            rating: 4.5,
            reviewCount: 12500,
            images: [
                'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=800',
                'https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?w=800',
                'https://images.unsplash.com/photo-1590490360182-c33d57733427?w=800',
            ],
            actionType: 'search',
            actionParams: { type: 'hotel', partner: 'marriott' },
        },
        enabled: true,
        createdAt: '2025-01-01T00:00:00Z',
        updatedAt: '2025-01-15T10:30:00Z',
    },
    {
        id: 'ad-2',
        campaignId: 'campaign-1',
        partnerId: 'partner-1',
        title: 'Marriott Weekend Getaway',
        description: 'Save up to 25% on weekend stays at select resorts.',
        image: 'https://images.unsplash.com/photo-1571896349842-33c89424de2d?w=400',
        ctaText: 'Book Now',
        tier: 'premium',
        category: 'hotel',
        inlineContent: {
            headline: 'Weekend Escape Sale',
            body: 'Escape for the weekend and save up to 25% at over 500 participating resorts. Limited time offer.',
            features: ['25% off weekend stays', 'Free breakfast included', 'Late checkout', 'Bonus points'],
            price: 'From $159/night',
            originalPrice: '$212/night',
            discount: '25% OFF',
            rating: 4.6,
            reviewCount: 8900,
            images: [
                'https://images.unsplash.com/photo-1571896349842-33c89424de2d?w=800',
            ],
            actionType: 'search',
            actionParams: { type: 'hotel', partner: 'marriott', promo: 'weekend25' },
        },
        enabled: true,
        createdAt: '2025-01-10T00:00:00Z',
        updatedAt: '2025-01-10T00:00:00Z',
    },

    // Delta Ads
    {
        id: 'ad-3',
        campaignId: 'campaign-2',
        partnerId: 'partner-2',
        title: 'Delta SkyMiles',
        description: 'Fly smarter. Earn miles on every trip.',
        image: 'https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=400',
        ctaText: 'Search Flights',
        tier: 'premium',
        category: 'flight',
        inlineContent: {
            headline: 'Miles That Take You Further',
            body: 'With Delta SkyMiles, every mile you earn brings you closer to your next adventure. No blackout dates, no seat restrictions.',
            features: ['No blackout dates', 'Miles never expire', '20+ partner airlines', 'Sky Club access available'],
            price: 'Earn 5x miles',
            rating: 4.3,
            reviewCount: 15600,
            images: [
                'https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=800',
                'https://images.unsplash.com/photo-1464037866556-6812c9d1c72e?w=800',
            ],
            actionType: 'search',
            actionParams: { type: 'flight', partner: 'delta' },
        },
        enabled: true,
        createdAt: '2025-02-01T00:00:00Z',
        updatedAt: '2025-02-01T00:00:00Z',
    },

    // Hertz Ads
    {
        id: 'ad-4',
        campaignId: 'campaign-3',
        partnerId: 'partner-3',
        title: 'Hertz Gold Plus',
        description: 'Skip the counter. Get rewards faster.',
        image: 'https://images.unsplash.com/photo-1449965408869-ebd3fee56fdf?w=400',
        ctaText: 'Rent a Car',
        tier: 'standard',
        category: 'car',
        inlineContent: {
            headline: 'The Fastest Way to Your Car',
            body: 'Hertz Gold Plus Rewards members skip the counter and go straight to their car. Join free and start earning today.',
            features: ['Skip the counter', 'Choose your car', 'Free additional driver', 'Points on every rental'],
            price: 'From $29/day',
            rating: 4.2,
            reviewCount: 9800,
            images: [
                'https://images.unsplash.com/photo-1449965408869-ebd3fee56fdf?w=800',
            ],
            actionType: 'search',
            actionParams: { type: 'car', partner: 'hertz' },
        },
        enabled: true,
        createdAt: '2025-02-01T00:00:00Z',
        updatedAt: '2025-02-01T00:00:00Z',
    },

    // Viator Ads (Featured - for Community full-screen)
    {
        id: 'ad-5',
        campaignId: 'campaign-4',
        partnerId: 'partner-4',
        title: 'Viator Experiences',
        description: 'Book top-rated tours & activities worldwide.',
        image: 'https://images.unsplash.com/photo-1533105079780-92b9be482077?w=400',
        ctaText: 'Explore Tours',
        tier: 'featured',
        category: 'activity',
        inlineContent: {
            headline: '🎭 Skip the Lines in Paris',
            body: 'Experience the best of Paris with skip-the-line access to the Eiffel Tower, Louvre, and more. Book now and save up to 20%.',
            features: ['Skip-the-line access', 'Expert local guides', 'Free cancellation', 'Best price guarantee'],
            price: 'From $25',
            discount: 'Up to 20% OFF',
            rating: 4.8,
            reviewCount: 45000,
            images: [
                'https://images.unsplash.com/photo-1533105079780-92b9be482077?w=800',
                'https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=800',
            ],
            actionType: 'search',
            actionParams: { type: 'activity', partner: 'viator', destination: 'paris' },
        },
        // For Community full-screen sponsored posts
        targetDestinations: ['Paris', 'France'],
        enabled: true,
        createdAt: '2025-01-15T00:00:00Z',
        updatedAt: '2025-02-12T09:15:00Z',
    },
    {
        id: 'ad-6',
        campaignId: 'campaign-4',
        partnerId: 'partner-4',
        title: 'Viator Food Tours',
        description: 'Taste your way through the world\'s best cuisines.',
        image: 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=400',
        ctaText: 'Book Food Tour',
        tier: 'featured',
        category: 'activity',
        inlineContent: {
            headline: '🍕 Authentic Local Food Tours',
            body: 'Discover hidden culinary gems with local food experts. From street food to fine dining, taste the authentic flavors of your destination.',
            features: ['10+ tastings included', 'Small group experience', 'Local expert guide', 'Vegetarian options'],
            price: 'From $45',
            rating: 4.9,
            reviewCount: 23000,
            images: [
                'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=800',
            ],
            actionType: 'search',
            actionParams: { type: 'activity', partner: 'viator', category: 'food' },
        },
        enabled: true,
        createdAt: '2025-01-20T00:00:00Z',
        updatedAt: '2025-01-20T00:00:00Z',
    },

    // GetYourGuide Ads
    {
        id: 'ad-7',
        campaignId: 'campaign-5',
        partnerId: 'partner-5',
        title: 'GetYourGuide',
        description: 'Discover hidden gems with local guides.',
        image: 'https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=400',
        ctaText: 'Find Experiences',
        tier: 'standard',
        category: 'activity',
        inlineContent: {
            headline: '🛕 Local Experiences Worldwide',
            body: 'Connect with passionate local guides and discover the stories behind every destination. Over 60,000 experiences in 170+ countries.',
            features: ['60,000+ experiences', 'Verified local guides', '24/7 customer support', 'Easy cancellation'],
            price: 'From $15',
            rating: 4.7,
            reviewCount: 38000,
            images: [
                'https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=800',
            ],
            actionType: 'search',
            actionParams: { type: 'activity', partner: 'getyourguide' },
        },
        enabled: true,
        createdAt: '2025-01-01T00:00:00Z',
        updatedAt: '2025-01-01T00:00:00Z',
    },

    // Allianz Ads (paused campaign)
    {
        id: 'ad-8',
        campaignId: 'campaign-6',
        partnerId: 'partner-6',
        title: 'Allianz Travel Insurance',
        description: 'Travel with peace of mind.',
        image: 'https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=400',
        ctaText: 'Get a Quote',
        tier: 'standard',
        category: 'insurance',
        inlineContent: {
            headline: 'Protect Your Trip',
            body: 'Comprehensive travel insurance covering trip cancellation, medical emergencies, lost luggage, and more. Get a free quote in seconds.',
            features: ['Trip cancellation coverage', 'Emergency medical coverage', 'Lost baggage protection', '24/7 assistance'],
            price: 'From $4.50/day',
            rating: 4.4,
            reviewCount: 12000,
            images: [
                'https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=800',
            ],
            actionType: 'learn_more',
            actionParams: { type: 'insurance', partner: 'allianz' },
        },
        enabled: true,
        createdAt: '2025-01-01T00:00:00Z',
        updatedAt: '2025-01-01T00:00:00Z',
    },
];

// --- Mock Ad Settings ---

export const MOCK_AD_SETTINGS: AdSettings = {
    globalEnabled: true,
    pageSettings: {
        community: true,
        my_trips: true,
        trip_details: true,
        my_photos: true,
        destinations: true,
    },
    tierSettings: {
        premium: true,
        standard: true,
        featured: true,
    },
    rotationIntervals: {
        premium: 45,
        standard: 30,
        featured: 60,
    },
    maxImpressionsPerAdPerSession: 3,
    delayBeforeFirstAd: 3,
};

// --- Mock Audit Log ---

export const MOCK_AUDIT_LOG: AdAuditLog[] = [
    {
        id: 'audit-1',
        entityType: 'campaign',
        entityId: 'campaign-6',
        action: 'disable',
        changes: [
            { field: 'status', oldValue: 'active', newValue: 'paused' },
        ],
        userId: 'admin-1',
        userName: 'Admin User',
        timestamp: '2025-02-10T16:00:00Z',
    },
    {
        id: 'audit-2',
        entityType: 'partner',
        entityId: 'partner-6',
        action: 'update',
        changes: [
            { field: 'status', oldValue: 'active', newValue: 'paused' },
        ],
        userId: 'admin-1',
        userName: 'Admin User',
        timestamp: '2025-02-10T16:00:00Z',
    },
    {
        id: 'audit-3',
        entityType: 'settings',
        entityId: 'page-my_photos',
        action: 'enable',
        changes: [
            { field: 'pageSettings.my_photos', oldValue: false, newValue: true },
        ],
        userId: 'admin-1',
        userName: 'Admin User',
        timestamp: '2025-02-08T11:30:00Z',
    },
    {
        id: 'audit-4',
        entityType: 'campaign',
        entityId: 'campaign-1',
        action: 'update',
        changes: [
            { field: 'budget', oldValue: 40000, newValue: 50000 },
        ],
        userId: 'admin-1',
        userName: 'Admin User',
        timestamp: '2025-02-15T10:00:00Z',
    },
];

// --- Helper Functions ---

export const getPartnerById = (id: string): AdPartner | undefined => {
    return MOCK_PARTNERS.find(p => p.id === id);
};

export const getCampaignById = (id: string): AdCampaign | undefined => {
    return MOCK_CAMPAIGNS.find(c => c.id === id);
};

export const getAdsByCampaign = (campaignId: string): Ad[] => {
    return MOCK_ADS.filter(a => a.campaignId === campaignId);
};

export const getAdsByPartner = (partnerId: string): Ad[] => {
    return MOCK_ADS.filter(a => a.partnerId === partnerId);
};

export const getCampaignsByPartner = (partnerId: string): AdCampaign[] => {
    return MOCK_CAMPAIGNS.find(c => c.partnerId === partnerId) ? 
        MOCK_CAMPAIGNS.filter(c => c.partnerId === partnerId) : [];
};

export const getActiveAdsForPage = (
    page: AdPage,
    placement: AdPlacement,
    settings: AdSettings
): Ad[] => {
    // Check global
    if (!settings.globalEnabled) return [];
    
    // Check page
    if (!settings.pageSettings[page]) return [];
    
    // Get active campaigns for this page/placement
    const activeCampaigns = MOCK_CAMPAIGNS.filter(c => {
        if (c.status !== 'active') return false;
        if (!c.pages.includes(page)) return false;
        if (!c.placements.includes(placement)) return false;
        
        // Check tier
        if (!settings.tierSettings[c.tier]) return false;
        
        // Check date range
        const now = new Date();
        const start = new Date(c.startDate);
        const end = new Date(c.endDate);
        if (now < start || now > end) return false;
        
        // Check partner status
        const partner = getPartnerById(c.partnerId);
        if (!partner || partner.status !== 'active') return false;
        
        return true;
    });
    
    // Get ads from active campaigns
    const campaignIds = new Set(activeCampaigns.map(c => c.id));
    return MOCK_ADS.filter(ad => ad.enabled && campaignIds.has(ad.campaignId));
};

// --- Stats Helpers ---

export const getAdStats = () => {
    const totalImpressions = MOCK_CAMPAIGNS.reduce((sum, c) => sum + c.impressions, 0);
    const totalClicks = MOCK_CAMPAIGNS.reduce((sum, c) => sum + c.clicks, 0);
    const totalSpent = MOCK_CAMPAIGNS.reduce((sum, c) => sum + (c.spent || 0), 0);
    const totalBudget = MOCK_CAMPAIGNS.reduce((sum, c) => sum + (c.budget || 0), 0);
    
    return {
        totalImpressions,
        totalClicks,
        ctr: totalImpressions > 0 ? ((totalClicks / totalImpressions) * 100).toFixed(2) : '0.00',
        totalSpent,
        totalBudget,
        activeCampaigns: MOCK_CAMPAIGNS.filter(c => c.status === 'active').length,
        activePartners: MOCK_PARTNERS.filter(p => p.status === 'active').length,
        totalAds: MOCK_ADS.filter(a => a.enabled).length,
    };
};
